// frontend/src/utils/helpers.js

import { devLogApiError } from './devLogApiError';

/**
 * Форматирование валюты
 */
export const formatCurrency = (value, currency = 'RUB', locale = 'ru-RU') => {
  if (value === null || value === undefined || isNaN(value)) {
    return '0 ₽';
  }

  return new Intl.NumberFormat(locale, {
    style: 'currency',
    currency: currency,
    minimumFractionDigits: 0,
    maximumFractionDigits: 2,
  }).format(Number(value));
};

/**
 * Форматирование чисел с разделителями
 */
export const formatNumber = (value, locale = 'ru-RU') => {
  if (value === null || value === undefined || isNaN(value)) {
    return '0';
  }

  return new Intl.NumberFormat(locale).format(Number(value));
};

/**
 * Безопасное приведение к числу (PG numeric часто приходит строкой).
 * Возвращает number или null (если не число).
 */
export const toFiniteNumber = (value) => {
  if (value === null || value === undefined || value === '') return null;
  const raw = typeof value === 'string' ? value.replace(',', '.') : value;
  const n = Number(raw);
  return Number.isFinite(n) ? n : null;
};

/**
 * Адаптивное форматирование дробных чисел:
 * - по умолчанию фиксированное число знаков (digits)
 * - если значение маленькое (0 < |x| < smallThreshold) — используем больше знаков (smallDigits)
 * - опционально триммим хвостовые нули
 */
export const formatAdaptiveNumber = (
  value,
  {
    digits = 2,
    smallDigits = 6,
    smallThreshold = null,
    trimZeros = true
  } = {}
) => {
  const n = toFiniteNumber(value);
  if (n == null) return null;

  const abs = Math.abs(n);
  const threshold = (smallThreshold != null) ? Number(smallThreshold) : Math.pow(10, -digits);
  const useDigits = (abs > 0 && abs < threshold) ? smallDigits : digits;

  let s = n.toFixed(useDigits);
  if (!trimZeros) return s;

  // trim trailing zeros: "1.230000" -> "1.23", "1.000" -> "1"
  s = s.replace(/(\.\d*?[1-9])0+$/u, '$1').replace(/\.0+$/u, '');
  return s;
};

/**
 * Форматирование даты
 */
export const formatDate = (date, options = {}) => {
  if (!date) return '';

  const defaultOptions = {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    ...options
  };

  return new Date(date).toLocaleDateString('ru-RU', defaultOptions);
};

/**
 * Форматирование времени
 */
export const formatDateTime = (date, options = {}) => {
  if (!date) return '';

  const defaultOptions = {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    ...options
  };

  return new Date(date).toLocaleString('ru-RU', defaultOptions);
};

/**
 * Получение относительного времени (например, "2 часа назад")
 */
export const getRelativeTime = (date) => {
  if (!date) return '';

  const now = new Date();
  const target = new Date(date);
  const diffMs = now - target;
  const diffMinutes = Math.floor(diffMs / (1000 * 60));
  const diffHours = Math.floor(diffMs / (1000 * 60 * 60));
  const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));

  if (diffMinutes < 1) return 'только что';
  if (diffMinutes < 60) return `${diffMinutes} мин. назад`;
  if (diffHours < 24) return `${diffHours} ч. назад`;
  if (diffDays < 7) return `${diffDays} дн. назад`;

  return formatDate(date);
};

/**
 * Обрезка текста с многоточием
 */
export const truncateText = (text, maxLength = 100) => {
  if (!text || text.length <= maxLength) {
    return text;
  }

  return text.substring(0, maxLength) + '...';
};

/**
 * Генерация случайного ID
 */
export const generateId = () => {
  return Math.random().toString(36).substr(2, 9);
};

/**
 * Debounce функция
 */
export const debounce = (func, wait) => {
  let timeout;
  return function executedFunction(...args) {
    const later = () => {
      clearTimeout(timeout);
      func(...args);
    };
    clearTimeout(timeout);
    timeout = setTimeout(later, wait);
  };
};

/**
 * Throttle функция
 */
export const throttle = (func, limit) => {
  let inThrottle;
  return function executedFunction(...args) {
    if (!inThrottle) {
      func.apply(this, args);
      inThrottle = true;
      setTimeout(() => inThrottle = false, limit);
    }
  };
};

/**
 * Копирование текста в буфер обмена
 */
export const copyToClipboard = async (text) => {
  try {
    if (navigator.clipboard && window.isSecureContext) {
      await navigator.clipboard.writeText(text);
      return true;
    } else {
      // Fallback для старых браузеров
      const textArea = document.createElement('textarea');
      textArea.value = text;
      textArea.style.position = 'fixed';
      textArea.style.left = '-999999px';
      textArea.style.top = '-999999px';
      document.body.appendChild(textArea);
      textArea.focus();
      textArea.select();

      const result = document.execCommand('copy');
      textArea.remove();
      return result;
    }
  } catch (error) {
    devLogApiError('буфер обмена: копирование текста', error);
    return false;
  }
};

/**
 * Загрузка файла
 */
export const downloadFile = (url, filename) => {
  const link = document.createElement('a');
  link.href = url;
  link.download = filename || 'download';
  link.style.display = 'none';
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
};

/**
 * Валидация email
 */
export const isValidEmail = (email) => {
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  return emailRegex.test(email);
};

/**
 * Валидация телефона (российский формат)
 */
export const isValidPhone = (phone) => {
  const phoneRegex = /^(\+7|7|8)?[\s-]?\(?[489][0-9]{2}\)?[\s-]?[0-9]{3}[\s-]?[0-9]{2}[\s-]?[0-9]{2}$/;
  return phoneRegex.test(phone);
};

/**
 * Форматирование телефона
 */
export const formatPhone = (phone) => {
  if (!phone) return '';

  const cleaned = phone.replace(/\D/g, '');

  if (cleaned.length === 11 && cleaned.startsWith('8')) {
    return `+7 (${cleaned.slice(1, 4)}) ${cleaned.slice(4, 7)}-${cleaned.slice(7, 9)}-${cleaned.slice(9)}`;
  } else if (cleaned.length === 11 && cleaned.startsWith('7')) {
    return `+7 (${cleaned.slice(1, 4)}) ${cleaned.slice(4, 7)}-${cleaned.slice(7, 9)}-${cleaned.slice(9)}`;
  } else if (cleaned.length === 10) {
    return `+7 (${cleaned.slice(0, 3)}) ${cleaned.slice(3, 6)}-${cleaned.slice(6, 8)}-${cleaned.slice(8)}`;
  }

  return phone;
};

/**
 * Получение цвета для статуса
 */
export const getStatusColor = (status) => {
  const statusColors = {
    // Общие статусы
    active: 'success',
    inactive: 'default',
    pending: 'warning',
    processing: 'processing',
    completed: 'success',
    failed: 'error',
    cancelled: 'error',

    // Статусы заказов
    new: 'blue',
    confirmed: 'cyan',
    shipped: 'geekblue',
    delivered: 'green',
    returned: 'orange',
    refunded: 'red',

    // Статусы синхронизации
    success: 'success',
    error: 'error',
    warning: 'warning',

    // Статусы товаров
    draft: 'default',
    published: 'success',
    archived: 'default',
  };

  return statusColors[status] || 'default';
};

/**
 * Получение иконки для статуса
 */
export const getStatusIcon = (status) => {
  const statusIcons = {
    active: 'check-circle',
    inactive: 'minus-circle',
    pending: 'clock-circle',
    processing: 'loading',
    completed: 'check-circle',
    failed: 'close-circle',
    cancelled: 'stop',

    new: 'file-add',
    confirmed: 'check',
    shipped: 'car',
    delivered: 'check-circle',
    returned: 'rollback',
    refunded: 'dollar',

    success: 'check-circle',
    error: 'exclamation-circle',
    warning: 'warning',

    draft: 'edit',
    published: 'eye',
    archived: 'folder',
  };

  return statusIcons[status] || 'question-circle';
};

/**
 * Конвертация размера файла в читаемый формат
 */
export const formatFileSize = (bytes) => {
  if (bytes === 0) return '0 Bytes';

  const k = 1024;
  const sizes = ['Bytes', 'KB', 'MB', 'GB', 'TB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));

  return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
};

/**
 * Проверка типа файла по расширению
 */
export const getFileType = (filename) => {
  if (!filename) return 'unknown';

  const extension = filename.split('.').pop().toLowerCase();

  const imageTypes = ['jpg', 'jpeg', 'png', 'gif', 'webp', 'svg'];
  const documentTypes = ['pdf', 'doc', 'docx', 'txt', 'rtf'];
  const spreadsheetTypes = ['xls', 'xlsx', 'csv'];
  const archiveTypes = ['zip', 'rar', '7z', 'tar', 'gz'];

  if (imageTypes.includes(extension)) return 'image';
  if (documentTypes.includes(extension)) return 'document';
  if (spreadsheetTypes.includes(extension)) return 'spreadsheet';
  if (archiveTypes.includes(extension)) return 'archive';

  return 'unknown';
};

/**
 * Сортировка массива объектов
 */
export const sortBy = (array, key, direction = 'asc') => {
  return [...array].sort((a, b) => {
    const aValue = a[key];
    const bValue = b[key];

    if (aValue < bValue) {
      return direction === 'asc' ? -1 : 1;
    }
    if (aValue > bValue) {
      return direction === 'asc' ? 1 : -1;
    }
    return 0;
  });
};

/**
 * Группировка массива по ключу
 */
export const groupBy = (array, key) => {
  return array.reduce((groups, item) => {
    const group = item[key];
    if (!groups[group]) {
      groups[group] = [];
    }
    groups[group].push(item);
    return groups;
  }, {});
};

/**
 * Удаление дубликатов из массива
 */
export const unique = (array, key = null) => {
  if (key) {
    const seen = new Set();
    return array.filter(item => {
      const value = item[key];
      if (seen.has(value)) {
        return false;
      }
      seen.add(value);
      return true;
    });
  }

  return [...new Set(array)];
};

/**
 * Глубокое клонирование объекта
 */
export const deepClone = (obj) => {
  if (obj === null || typeof obj !== 'object') {
    return obj;
  }

  if (obj instanceof Date) {
    return new Date(obj.getTime());
  }

  if (obj instanceof Array) {
    return obj.map(item => deepClone(item));
  }

  if (typeof obj === 'object') {
    const cloned = {};
    for (const key in obj) {
      if (obj.hasOwnProperty(key)) {
        cloned[key] = deepClone(obj[key]);
      }
    }
    return cloned;
  }

  return obj;
};

/**
 * Проверка, является ли объект пустым
 */
export const isEmpty = (obj) => {
  if (obj === null || obj === undefined) return true;
  if (typeof obj === 'string' || Array.isArray(obj)) return obj.length === 0;
  if (typeof obj === 'object') return Object.keys(obj).length === 0;
  return false;
};

/**
 * ==========================================
 * СКЛАДЫ: единый формат отображения (UI)
 * ==========================================
 *
 * Требование UX:
 * - показываем понятное название склада
 * - если есть поставщик — добавляем в квадратных скобках: "Склад A [Поставщик]"
 * - не показываем "Склад {id}" как основной label
 */

export const resolveWarehouseName = (warehouse) => {
  const w = warehouse && typeof warehouse === 'object' ? warehouse : {};
  const name = (
    w.name ||
    w.warehouse_name ||
    w.external_warehouse_name ||
    w.externalWarehouseName ||
    w.display_name ||
    w.displayName ||
    ''
  );
  const s = String(name || '').trim();
  return s || null;
};

export const resolveSupplierNameForWarehouse = (warehouse) => {
  const w = warehouse && typeof warehouse === 'object' ? warehouse : {};
  const sup = (
    w.supplier_name ||
    w.supplierName ||
    w.supplier ||
    w.partner_name ||
    w.partnerName ||
    ''
  );
  const s = String(sup || '').trim();
  return s || null;
};

export const formatWarehouseLabel = (warehouse, { supplierName = null, includeSupplier = true } = {}) => {
  const name = resolveWarehouseName(warehouse);
  const sup = (supplierName != null ? String(supplierName).trim() : null) || resolveSupplierNameForWarehouse(warehouse);

  // Если имени нет — лучше показать идентификатор склада, чем голое "Склад"
  const w = warehouse && typeof warehouse === 'object' ? warehouse : {};
  const fallbackId = (
    w.external_warehouse_name ||
    w.external_warehouse_id ||
    w.warehouse_external_id ||
    w.externalWarehouseId ||
    w.warehouse_id ||
    w.warehouseId ||
    w.code ||
    w.id ||
    null
  );
  const base = name || (fallbackId != null && String(fallbackId).trim() ? String(fallbackId).trim() : 'Склад');
  if (includeSupplier !== true) return base;
  if (!sup) return base;

  // Избегаем дублей вида "Склад A (Поставщик)" / "Склад A [Поставщик]" -> не добавляем ещё раз
  if (base.includes(`(${sup})`) || base.includes(`[${sup}]`)) return base;
  return `${base} (${sup})`;
};

/**
 * Нормализация пробелов в тексте:
 * - Удаляет пробелы в начале и конце
 * - Заменяет множественные пробелы на один
 * @param {string} text - Текст для нормализации
 * @returns {string} Нормализованный текст
 */
export const normalizeSpaces = (text) => {
  if (!text || typeof text !== 'string') return '';
  return text.trim().replace(/\s+/g, ' ');
};

/**
 * Группировка остатков поставщиков по supplier_id.
 * Возвращает массив { supplierName, items[] } для отображения в модалке остатков.
 * @param {Array} rows - массив записей остатков (каждая с supplier_id, supplier_name, …)
 * @returns {Array<{supplierName: string, items: Array}>}
 */
export const groupSupplierStocksByWarehouseAndQty = (rows) => {
  if (!Array.isArray(rows) || rows.length === 0) return [];

  const map = new Map();
  for (const row of rows) {
    const key = String(row.supplier_id || 'unknown');
    if (!map.has(key)) {
      map.set(key, {
        supplierName: row.supplier_name || row.supplierName || 'Поставщик',
        items: [],
      });
    }
    map.get(key).items.push(row);
  }

  return Array.from(map.values());
};